// TUNE Analytics' pages.
import { Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams, type RouteObject } from 'react-router';
import { chunk, lazyPage } from './lazy';
import { CarLibrary } from './features/car/CarLibrary';
import { Connections } from './features/car/Connections';
import { Recordings } from './features/car/Recordings';
import { Service } from './features/car/Service';
import { Settings } from './features/car/Settings';
import { SettingsHistory } from './features/car/SettingsHistory';
import { AccountPage } from './features/account/Account';
import { CreateHub } from './features/create/CreateHub';
import { Broken, NotFound } from './features/errors/Errors';
import { Dashboards } from './features/create/Dashboards';
import { LibraryItem } from './features/library/Item';
import { Pair } from './features/pair/Pair';
import { Catalogue, Library, SharedWithYou } from './features/library/Library';
import { Telemetry } from './features/telemetry/Telemetry';
import { Car } from './features/garage/Car';
import { Garage } from './features/garage/Garage';
import { Help } from './features/help/Help';
import { Front } from './features/home/Front';
import { Spinner } from './ui';
import { RequireSignIn } from './require';
import { Moved, MovedCar, MovedPairCode, MovedStaff, Root, type FrameHandle } from './shell/Root';

// The Analyser loads when it's first opened, with its charts and map.
const analyse = () => import('./features/telemetry/Analyse');
const AnalyseDatalog = lazyPage(analyse, (m) => m.AnalyseDatalog);
const AnalyseFile = lazyPage(analyse, (m) => m.AnalyseFile);
const AnalyseSample = lazyPage(analyse, (m) => m.AnalyseSample);

// The config creator, with the config reader and checker, the same way.
const creator = () => import('./features/create/config/pages');
const ConfigEditor = lazyPage(creator, (m) => m.ConfigEditor);
const NewConfig = lazyPage(creator, (m) => m.NewConfig);
const OpenConfig = lazyPage(creator, (m) => m.OpenConfig);

function Later({ label = 'Opening the Analyser', children }: { label?: string; children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="page-waiting">
          <Spinner size="large" label={label} />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/** `/groups/:groupId`: groups are in the admin dashboard now. */
function MovedGroup() {
  const { groupId = '' } = useParams();
  return <Moved to={`/admin/groups/${groupId}`} />;
}

/** A page of the config creator. */
const creating = (page: ReactNode) => <Later label="Opening the config creator">{page}</Later>;

/** A page for owners signed in: anyone else signs in first, and comes back. */
const owners = (route: RouteObject): RouteObject => ({
  ...route,
  element: <RequireSignIn pool="owners">{route.element}</RequireSignIn>,
});

/** Nothing, while a page that loads when it's opened is on its way. */
function Nothing() {
  return null;
}

type AuthPages = typeof import('./auth/pages');

/** A sign-in page, from the sign-in pages' chunk. */
function authPage(path: string, pick: (pages: AuthPages) => ComponentType): RouteObject {
  return {
    path,
    HydrateFallback: Nothing,
    lazy: async () => ({ Component: pick(await chunk(() => import('./auth/pages'))) }),
  };
}

type AdminPages = typeof import('./features/admin/pages');

/** A page of the admin dashboard, from its chunk: owners never load it. */
function adminPage(route: RouteObject, pick: (pages: AdminPages) => ComponentType): RouteObject {
  return {
    ...route,
    HydrateFallback: Nothing,
    lazy: async () => ({ Component: pick(await chunk(() => import('./features/admin/pages'))) }),
  };
}

/** The pages that sign people in: TUNE Analytics' own, outside the frame. */
const signInRoutes: RouteObject[] = [
  authPage('sign-in', (p) => p.SignIn),
  authPage('sign-up', (p) => p.SignUp),
  authPage('sign-up/verify', (p) => p.Verify),
  authPage('forgot-password', (p) => p.OwnersForgot),
  authPage('forgot-password/reset', (p) => p.OwnersReset),
  authPage('join/:token', (p) => p.Join),
  authPage('admin/sign-in', (p) => p.StaffSignIn),
  authPage('admin/forgot-password', (p) => p.StaffForgot),
  authPage('admin/forgot-password/reset', (p) => p.StaffReset),
  // Where Cognito's own pages sent people back, before TUNE Analytics signed them in itself.
  { path: 'signed-in', element: <Navigate to="/" replace /> },
  { path: 'staff/signed-in', element: <Navigate to="/admin" replace /> },
];

/** Pages only on this machine: the gallery of TUNE Analytics' parts. A production build leaves them out. */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: '_dev/components',
        HydrateFallback: Nothing,
        lazy: async () => ({ Component: (await import('./features/dev/gallery')).Gallery }),
      },
    ]
  : [];

/** What a route asks of the frame (shell/Root.tsx). */
const PUBLIC: FrameHandle = { chrome: 'public' };
const PUBLIC_IMMERSIVE: FrameHandle = { chrome: 'publicImmersive' };
const IMMERSIVE: FrameHandle = { chrome: 'immersive' };
const FOCUSED: FrameHandle = { chrome: 'focused' };

export const router = createBrowserRouter([
  ...devRoutes,
  ...signInRoutes,
  // The admin dashboard: TUNE's staff, and the admins TUNE makes.
  adminPage(
    {
      path: 'admin',
      errorElement: <Broken />,
      children: [
        adminPage({ index: true }, (p) => p.Overview),
        adminPage({ path: 'invites' }, (p) => p.Invites),
        adminPage({ path: 'groups' }, (p) => p.Groups),
        adminPage({ path: 'groups/:groupId' }, (p) => p.GroupPage),
        adminPage({ path: 'activity' }, (p) => p.StaffActivity),
        adminPage({ path: 'more' }, (p) => p.StaffMore),
        adminPage({ path: 'accounts' }, (p) => p.StaffAccounts),
        adminPage({ path: 'accounts/:accountId' }, (p) => p.StaffAccounts),
        adminPage({ path: 'catalogue' }, (p) => p.StaffCatalogue),
        adminPage({ path: 'catalogue/:itemId' }, (p) => p.StaffCatalogueItem),
        adminPage({ path: 'support' }, (p) => p.StaffSupport),
        adminPage({ path: 'support/:reference' }, (p) => p.StaffSupport),
        adminPage({ path: 'bulletin' }, (p) => p.StaffBulletin),
        adminPage({ path: 'policy' }, (p) => p.StaffPolicy),
        { path: '*', element: <NotFound admin /> },
      ],
    },
    (p) => p.Dashboard,
  ),
  // The staff's old addresses: the dashboard has their pages now.
  { path: 'staff/*', element: <MovedStaff /> },
  {
    element: <Root />,
    errorElement: <Broken />,
    children: [
      { index: true, handle: PUBLIC, element: <Front /> },
      { path: 'help', handle: PUBLIC, element: <Help /> },
      owners({ path: 'pair', handle: FOCUSED, element: <Pair /> }),
      { path: 'pair/:code', element: <MovedPairCode /> },
      owners({ path: 'garage', element: <Garage /> }),
      owners({
        path: 'garage/:vehicleId',
        element: <Outlet />,
        children: [
          { index: true, element: <Car /> },
          { path: 'settings', element: <Settings /> },
          { path: 'settings/history', element: <SettingsHistory /> },
          { path: 'connections', element: <Connections /> },
          { path: 'service', element: <Service /> },
          { path: 'library', element: <CarLibrary /> },
          { path: 'recordings', element: <Recordings /> },
        ],
      }),
      owners({ path: 'telemetry', element: <Telemetry /> }),
      // A file from this computer and TUNE's sample are read in the browser, so they need no account.
      {
        path: 'telemetry/file',
        handle: PUBLIC_IMMERSIVE,
        element: (
          <Later>
            <AnalyseFile />
          </Later>
        ),
      },
      {
        path: 'telemetry/sample',
        handle: PUBLIC_IMMERSIVE,
        element: (
          <Later>
            <AnalyseSample />
          </Later>
        ),
      },
      owners({
        path: 'telemetry/:vehicleId/:datalogId',
        handle: IMMERSIVE,
        element: (
          <Later>
            <AnalyseDatalog />
          </Later>
        ),
      }),
      owners({ path: 'library', element: <Library /> }),
      owners({ path: 'library/shared', element: <SharedWithYou /> }),
      owners({ path: 'library/catalogue', element: <Catalogue /> }),
      owners({ path: 'library/:itemId', element: <LibraryItem /> }),
      owners({ path: 'create', element: <CreateHub /> }),
      owners({ path: 'create/dashboards', element: <Dashboards /> }),
      owners({ path: 'create/configs/new', element: creating(<NewConfig />) }),
      owners({ path: 'create/configs/:itemId', element: creating(<OpenConfig />) }),
      owners({ path: 'create/configs/drafts/:draftId', handle: IMMERSIVE, element: creating(<ConfigEditor />) }),
      owners({ path: 'account', element: <AccountPage /> }),
      { path: 'groups', element: <Moved to="/admin/groups" /> },
      { path: 'groups/:groupId', element: <MovedGroup /> },
      // The old addresses, each where its page is now, with its search and fragment.
      { path: 'vehicles/:vehicleId/*', element: <MovedCar /> },
      { path: 'shared', element: <Moved to="/library/shared" /> },
      { path: 'catalogue', element: <Moved to="/library/catalogue" /> },
      { path: 'tools', element: <Moved to="/create" /> },
      { path: 'tools/config-editor', element: <Moved to="/create/configs/new" /> },
      { path: 'tools/theme-editor', element: <Moved to="/create/dashboards" /> },
      { path: 'contact', element: <Moved to="/help" /> },
      { path: '*', handle: PUBLIC, element: <NotFound /> },
    ],
  },
]);
